import { SupabaseClient } from '@supabase/supabase-js';
import { BaseRepository } from './base.repository';
import { Room, RoomInsert, RoomUpdate } from '../entities/room';
import { PaginationOptions, PaginatedResponse } from '../types/pagination.types';
import { applyPagination } from '../utils/pagination.utils';

export class RoomRepository extends BaseRepository<Room, RoomInsert, RoomUpdate> {
  constructor(supabase: SupabaseClient) {
    super(supabase, 'rooms');
  }

  /**
   * Save a Room entity (create or update based on dirty state)
   */
  async save(room: Room): Promise<Room> {
    if (!room.isDirty()) {
      return room; // No changes to save
    }

    const data = room.toData();
    const { id, created_at, ...updateData } = data; // Remove fields that shouldn't be updated

    const { data: saved, error } = await this.supabase
      .from(this.tableName)
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Create new entity instance from saved data
    const savedRoom = Room.fromData(saved);
    savedRoom.markAsClean();
    return savedRoom;
  }

  /**
   * Override findById to return Room entity
   */
  async findById(id: string): Promise<Room | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }
    
    return data ? Room.fromData(data) : null;
  }

  /**
   * Override create to return Room entity
   */
  async create(data: RoomInsert): Promise<Room> {
    const { data: created, error } = await this.supabase
      .from(this.tableName)
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return Room.fromData(created);
  }

  /**
   * Find rooms by user
   */
  async findByUser(userId: string): Promise<Room[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('user_id', userId)
      .order('last_message_at', { ascending: false, nullsFirst: false });

    if (error) throw error;
    return (data || []).map(d => Room.fromData(d));
  }

  /**
   * Find rooms by user (paginated)
   */
  async findByUserPaginated(userId: string, options: PaginationOptions): Promise<PaginatedResponse<Room>> {
    const query = this.supabase
      .from(this.tableName)
      .select('*')
      .eq('user_id', userId);

    // Set default orderBy if not provided
    const finalOptions = {
      ...options,
      orderBy: options.orderBy || 'last_message_at',
      order: options.order || 'desc' as const
    };

    const result = await applyPagination(query, finalOptions, (d) => Room.fromData(d));
    if ('pagination' in result) {
      return result;
    }
    throw new Error('Expected paginated response');
  }

  /**
   * Find rooms by project
   */
  async findByProject(projectId: string): Promise<Room[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .order('last_message_at', { ascending: false, nullsFirst: false });

    if (error) throw error;
    return (data || []).map(d => Room.fromData(d));
  }

  /**
   * Find rooms by project (paginated)
   */
  async findByProjectPaginated(projectId: string, options: PaginationOptions): Promise<PaginatedResponse<Room>> {
    const query = this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId);

    // Set default orderBy if not provided
    const finalOptions = {
      ...options,
      orderBy: options.orderBy || 'last_message_at',
      order: options.order || 'desc' as const
    };

    const result = await applyPagination(query, finalOptions, (d) => Room.fromData(d));
    if ('pagination' in result) {
      return result;
    }
    throw new Error('Expected paginated response');
  }

  /**
   * Find rooms by user and project
   */
  async findByUserAndProject(userId: string, projectId: string): Promise<Room[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('user_id', userId)
      .eq('project_id', projectId)
      .order('last_message_at', { ascending: false, nullsFirst: false });

    if (error) throw error;
    return (data || []).map(d => Room.fromData(d));
  }

  /**
   * Find rooms by user and project (paginated)
   */
  async findByUserAndProjectPaginated(userId: string, projectId: string, options: PaginationOptions): Promise<PaginatedResponse<Room>> {
    const query = this.supabase
      .from(this.tableName)
      .select('*', { count: 'exact' })
      .eq('user_id', userId)
      .eq('project_id', projectId);

    // Set default orderBy if not provided
    const finalOptions = {
      ...options,
      orderBy: options.orderBy || 'last_message_at',
      order: options.order || 'desc' as const
    };

    const result = await applyPagination(query, finalOptions, (d) => Room.fromData(d));
    if ('pagination' in result) {
      return result;
    }
    throw new Error('Expected paginated response');
  }

  /**
   * Get room with latest chat preview
   */
  async findWithLatestChat(roomId: string): Promise<{ room: Room; latestChat: any } | null> {
    const { data: roomData, error: roomError } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('id', roomId)
      .single();

    if (roomError) {
      if (roomError.code === 'PGRST116') return null;
      throw roomError;
    }

    const { data: chatData, error: chatError } = await this.supabase
      .from('chats')
      .select('*')
      .eq('room_id', roomId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (chatError && chatError.code !== 'PGRST116') {
      throw chatError;
    }

    return {
      room: Room.fromData(roomData),
      latestChat: chatData || null
    };
  }

  /**
   * Search rooms by name
   */
  async searchByName(userId: string, searchTerm: string): Promise<Room[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('user_id', userId)
      .ilike('name', `%${searchTerm}%`)
      .order('last_message_at', { ascending: false, nullsFirst: false });

    if (error) throw error;
    return (data || []).map(d => Room.fromData(d));
  }

  /**
   * Search rooms by name (paginated)
   */
  async searchByNamePaginated(userId: string, searchTerm: string, options: PaginationOptions): Promise<PaginatedResponse<Room>> {
    const query = this.supabase
      .from(this.tableName)
      .select('*')
      .eq('user_id', userId)
      .ilike('name', `%${searchTerm}%`);

    // Set default orderBy if not provided
    const finalOptions = {
      ...options,
      orderBy: options.orderBy || 'last_message_at',
      order: options.order || 'desc' as const
    };

    const result = await applyPagination(query, finalOptions, (d) => Room.fromData(d));
    if ('pagination' in result) {
      return result;
    }
    throw new Error('Expected paginated response');
  }

  /**
   * Update room's last message timestamp
   */
  async updateLastMessageAt(roomId: string): Promise<void> {
    const { error } = await this.supabase
      .from(this.tableName)
      .update({ last_message_at: new Date().toISOString() })
      .eq('id', roomId);

    if (error) throw error;
  }
}